import { memo, useCallback, ChangeEvent } from 'react';
import classNames from 'classnames';
import { BiMinusCircle } from 'react-icons/bi';
import {
  V2QsSelectCountryProps,
  V2QsCountryOption,
  defaultV2QsSelectCountryProps,
} from './types';

/**
 * V2QsSelectCountry 컴포넌트
 *
 * 국가 선택을 위한 드롭다운 컴포넌트
 * V1 QsInputSelectCountry의 V2 버전으로 순수 UI만 담당
 *
 * 주요 기능:
 * - 드롭다운으로 국가 선택
 * - 단일/다중 선택 지원
 * - 다중 선택 시 선택된 국가 목록 표시 + 삭제 버튼
 */
function V2QsSelectCountry(props: V2QsSelectCountryProps) {
  const {
    options,
    value,
    onChange,
    multi = defaultV2QsSelectCountryProps.multi,
    disabled = defaultV2QsSelectCountryProps.disabled,
    readOnly = defaultV2QsSelectCountryProps.readOnly,
    hasError = defaultV2QsSelectCountryProps.hasError,
    errorMessage,
    placeholder = defaultV2QsSelectCountryProps.placeholder,
    className,
    selectClassName,
    autoFocus,
  } = props;

  // 다중 선택 시 배열로 변환
  const getSelectedValues = (): string[] => {
    if (!multi) return [];
    if (Array.isArray(value)) return value;
    return value ? [value] : [];
  };
  const selectedValues: string[] = getSelectedValues();

  // 단일 선택 시 값
  const singleValue = !multi && typeof value === 'string' ? value : '';

  // 옵션 라벨 조회
  const getOptionLabel = useCallback(
    (optionValue: string): string => {
      const option = options.find((opt) => opt.value === optionValue);
      return option?.label || optionValue;
    },
    [options]
  );

  // 다중 선택 - 선택 변경
  const handleMultiChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      if (!onChange || disabled || readOnly) return;

      const newValue = e.target.value;
      if (!newValue) return;

      // 이미 선택된 경우 제거, 아니면 추가
      const isSelected = selectedValues.includes(newValue);
      let newValues: string[];

      if (isSelected) {
        newValues = selectedValues.filter((v) => v !== newValue);
      } else {
        newValues = [...selectedValues, newValue];
      }

      onChange(newValues);

      // 선택 후 드롭다운 초기화 (빈 값으로)
      e.target.value = '';
    },
    [onChange, disabled, readOnly, selectedValues]
  );

  // 다중 선택 - 항목 삭제
  const handleRemoveValue = useCallback(
    (valueToRemove: string) => {
      if (!onChange || disabled || readOnly) return;

      const newValues = selectedValues.filter((v) => v !== valueToRemove);
      onChange(newValues);
    },
    [onChange, disabled, readOnly, selectedValues]
  );

  // 단일 선택 - 선택 변경
  const handleSingleChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      if (!onChange || disabled || readOnly) return;
      onChange(e.target.value);
    },
    [onChange, disabled, readOnly]
  );

  // 다중 선택 UI
  if (multi) {
    return (
      <div className={classNames('flex flex-col', className)}>
        {/* 선택된 국가 목록 */}
        {selectedValues.length > 0 && (
          <ul className="pb-2">
            {selectedValues.map((selectedValue) => (
              <li
                key={selectedValue}
                className="flex items-center justify-between py-2"
              >
                <div>{getOptionLabel(selectedValue)}</div>
                <button
                  type="button"
                  disabled={disabled || readOnly}
                  onClick={() => handleRemoveValue(selectedValue)}
                  className={classNames(
                    'cursor-pointer',
                    (disabled || readOnly) && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <BiMinusCircle size={23} className="text-[#4B7BF5]" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* 드롭다운 */}
        <select
          className={classNames(
            'select select-bordered w-full',
            hasError && 'select-error',
            selectClassName
          )}
          disabled={disabled}
          onChange={handleMultiChange}
          value=""
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* 에러 메시지 */}
        {hasError && errorMessage && (
          <p className="text-error text-sm mt-1">{errorMessage}</p>
        )}
      </div>
    );
  }

  // 단일 선택 UI
  return (
    <div className={classNames('flex flex-col', className)}>
      <select
        className={classNames(
          'select select-bordered w-full',
          hasError && 'select-error',
          selectClassName
        )}
        disabled={disabled}
        value={singleValue}
        onChange={handleSingleChange}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* 에러 메시지 */}
      {hasError && errorMessage && (
        <p className="text-error text-sm mt-1">{errorMessage}</p>
      )}
    </div>
  );
}

export default memo(V2QsSelectCountry);
